import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, setDisplay, toggleClass, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_ITEM } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { isBetween, toPx } from "@cervval.js/common/src/tools/number.tools";
import { HOURS_IN_ONE_DAY, MINUTES_IN_ONE_DAY, MINUTES_IN_ONE_HOUR, SECONDS_IN_ONE_DAY, SECONDS_IN_ONE_HOUR, convertTimeInMinutesToDaysHoursMinutes, hoursToMinutes, minutesToSeconds, secondsToMinutes } from "@cervval.js/common/src/tools/time.tools";
import { HOURS, MILLISECONDS, MINUTES, MINUTES_OR_SECONDS, TIME_IN_MINUTES, TIME_IN_SECONDS } from "@cervval.js/common/src/tools/type.tools";
import { NumberTools } from "@cervval.js/common/tools";
import { Picker, PickerOptions } from "../picker";
import { I18nType, i18n } from "./i18n/i18n";

const MAX_HOURS_DAY_LIMIT: HOURS = HOURS_IN_ONE_DAY;
export const TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT: HOURS = 23; // By default: pick between 0 and 23hours

export interface TimePickerRange {

    /** Start of range, included */
    startTime: TIME_IN_MINUTES | TIME_IN_SECONDS;

    /** End of range, included */
    finishTime: TIME_IN_MINUTES | TIME_IN_SECONDS;
}

export interface TimePickerOptions extends PickerOptions {

    /** 
     * Selected mode of the TimePicker (default: false for 24h) 
     * ! Exclusive with disableDayHoursLimits !
     */
    hourAmPm: boolean;

    /** Display time header units */
    displayHeaderUnits: boolean;

    /** Display selector header units */
    displaySelectorHeaderUnits: boolean;

    /** Enable time selectors for hours and minutes */
    enableTimeSelectors: boolean;

    /** Ranges of time for selection */
    ranges?: TimePickerRange[];

    /** Time picker will use seconds as value unit instead of minutes */
    useSeconds?: boolean;

    /** 
     * Limit of max hours, default === 23,
     * With this option, user can pick between 0 and "maxHoursLimit" hours
     * ! Exclusive with hourAmPm !
     */
    maxHoursLimit?: HOURS;

    /** 
     * Allow negative hours
     * With this option you can return value in past 
     * If value is equal to "-3h", rendered as: "21:00 (J-1))""
     */
    minNegativeHoursLimit?: HOURS;

    // TODO Headers colors in options
}

export const defaultTimePickerOptions: TimePickerOptions = {

    /** Selected mode of the TimePicker (default: false for 24h) */
    hourAmPm: false,

    /** Display time header units */
    displayHeaderUnits: true,

    /** Display selector header units */
    displaySelectorHeaderUnits: true,

    /** Enable time selectors for hours and minutes */
    enableTimeSelectors: true,

    /** 
     * Limit of max hours, default === 23,
     * With this option, user can pick between 0 and "maxHoursLimit" hours
     * ! Exclusive with hourAmPm !
     */
    maxHoursLimit: TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT,

    /** @inheritdoc */
    disabled: false

    // TODO Headers colors in options
};

export type AmOrPm = "am" | "pm";
export type TimePickerSelectorMode = "hr" | "min";

/** Type of detail for SetTimeInMinuteTimePickerEvent event */
export interface TimePickerSetTimeInMinuteTimePickerEvent {
    /** Is time am or pm */
    ampm: AmOrPm;
}

const ARROW_DISABLED = "arrow-disabled";
const ENABLE_SELECTOR_CLASS = "selector-enable";
const AM_PM_COLUMN_DIV_CLASS = "ampm-column-div";
const SELECTOR_DISABLED = "selector-disabled";


@CustomElement({
    name: "cjs-time-picker",
    style: require("./timePicker.scss"),
    template: require("./timePicker.html"),
    i18n,
    elementCssClasses: [FLEX_ITEM]
})
export class CjsTimePicker extends Picker<number, I18nType> {

    @TypedWebComponentOptions(defaultTimePickerOptions)
    public override readonly options!: WebComponentOptions<TimePickerOptions>;

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    @Event(NoBubblesEventInit)
    public setTimeInMinuteTimePickerEvent!: EventHandler<TimePickerSetTimeInMinuteTimePickerEvent>;

    /** Time value in minutes or seconds of picker */
    protected override _value: MINUTES_OR_SECONDS = 0;

    /** Click pressed debounce delay value in ms */
    protected readonly MOUSE_CLICK_DEBOUNCE: MILLISECONDS = 200;

    /**
     * The timeDisplayed of the TimePicker
     */
    private _timeDisplayed = {
        hours: 0,
        minutes: 0
    };

    /** 
     * The state of hour is case hourAmPm is enable, it initialize with the current timeInMinutes of the TimePicker
     * default: empty
     */
    private _amORpm: AmOrPm = "am";

    /** 
     * The components of the TimePicker
     */
    @TypedHtmlElements()
    private readonly _components!: {
        timePickerContainer: HTMLElement,
        timeDisplay: HTMLElement,
        headerUnits: HTMLElement,
        hrUpBtn: HTMLElement,
        hrDownBtn: HTMLElement,
        minUpBtn: HTMLElement,
        minDownBtn: HTMLElement,
        hoursValue: HTMLElement,
        minutesValue: HTMLElement,
        radioModeAmPm: HTMLElement,
        amPmBtn: HTMLElement,
        amPmValue: HTMLElement,
        hoursSelectorContainer: HTMLElement,
        minutesSelectorContainer: HTMLElement,
        hoursSelectorUndo: HTMLElement,
        minutesSelectorUndo: HTMLElement,
        hoursSelector: HTMLElement,
        minutesSelector: HTMLElement,
        hoursSelectorHoursLabel: HTMLDivElement,
        hoursSelectorMinutesLabel: HTMLDivElement,
        timeDisplayHoursLabel: HTMLDivElement,
        timeDisplayMinutesLabel: HTMLDivElement
    };

    constructor(options?: Partial<TimePickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Initialize hour and minute list in each Popup
     * Add all event listenner to manipulate the TimePicker (up and down bouton, wheel, popupList, displayedTime)
     * Then show display the current time of the TimePicker
     */
    public override init(): void {

        this._components.hrUpBtn.onclick = () => this._incHr();
        this._components.hrDownBtn.onclick = () => this._downHr();
        this._components.minUpBtn.onclick = () => this._incMin();
        this._components.minDownBtn.onclick = () => this._downMin();

        // change time with arrow click hold
        this._addHrOrMinButtonClickListener(this._components.hrUpBtn);
        this._addHrOrMinButtonClickListener(this._components.hrDownBtn);
        this._addHrOrMinButtonClickListener(this._components.minUpBtn);
        this._addHrOrMinButtonClickListener(this._components.minDownBtn);

        // Display the default TimePicker view when we clicked on undo of timeSelector
        this._components.hoursSelectorUndo.onclick = (e: Event) => this.resetTimePickerView();
        this._components.minutesSelectorUndo.onclick = (e: Event) => this.resetTimePickerView();

        // Selection of hour/minute in selector
        this._components.hoursSelector.onclick = (event: MouseEvent) => this._onTimeSelectorClick(event, "hr");
        this._components.minutesSelector.onclick = (event: MouseEvent) => this._onTimeSelectorClick(event, "min");

        // Change time with mousewheel
        this._components.hoursValue.onwheel = (event: WheelEvent) => this._wheelHr(event);
        this._components.minutesValue.onwheel = (event: WheelEvent) => this._wheelMinute(event);
    }

    /** Refresh component */
    public override refresh(): void {
        super.refresh();

        this._setTimeDisplayedFromTime();

        // Set _timeDisplayed of the component
        if (!this._isValidTime(this._value)) {
            this._value = 0;
        }

        // Show popupList on click of displayed time
        if (this.options.enableTimeSelectors) {

            // Initialize list of element of hours and minutes
            this._initHourSelector();
            this._initMinutesSelector();
            this._setTimeDisplayedFromTime();

            this._components.hoursValue.onclick = (event: MouseEvent) => this._timeSelectorHandler("hr", event);
            this._components.minutesValue.onclick = (event: MouseEvent) => this._timeSelectorHandler("min", event);
        } else {
            this._components.hoursValue.onclick = null;
            this._components.minutesValue.onclick = null;
        }
        toggleClass(this._components.hoursValue, this.options.enableTimeSelectors, ENABLE_SELECTOR_CLASS);
        toggleClass(this._components.minutesValue, this.options.enableTimeSelectors, ENABLE_SELECTOR_CLASS);

        // Handle header units display
        toggleVisible(this._components.headerUnits, this.options.displayHeaderUnits, Display.FLEX);

        // Set Handler on the radio mode button to change time (am/pm mode)
        if (this.options.hourAmPm) {

            // Update amOrPm value
            this._amORpm = this.isAmOrPm(this._getValueInMinutes());

            this._components.amPmBtn.onclick = (event: Event) => this._onClickAmPmBtn(event);

            setDisplay(this._components.radioModeAmPm, Display.FLEX);
            const amPmColumnDivs = this._components.timePickerContainer.querySelectorAll("." + AM_PM_COLUMN_DIV_CLASS);
            amPmColumnDivs.forEach((amPmDiv) => {
                setDisplay(amPmDiv as HTMLElement, Display.FLEX);
            });
        } else {
            setDisplay(this._components.radioModeAmPm, Display.NONE);

            const amPmColumnDivs = this._components.timePickerContainer.querySelectorAll("." + AM_PM_COLUMN_DIV_CLASS);
            amPmColumnDivs.forEach((amPmDiv) => {
                setDisplay(amPmDiv as HTMLElement, Display.NONE);
            });
        }

        // Refresh behavior based on ranges
        this._refreshDisplayFromRanges();

        // Refresh i18n labels
        this._refreshI18nLabels();

        // Show the current time
        this.updateDisplayedTime();

        // Check incompatible options, log a warning if given
        if (this.options.hourAmPm && this.options.maxHoursLimit !== TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT) {
            const optionsExclusive = this.i18n.errorLog.optionsExclusive;
            this._log?.warn(`${optionsExclusive.option} "hourAmPm" ${optionsExclusive.and} "disableDayHoursLimits" ${optionsExclusive.warning}`);
        }
    }

    /**
     * Setter time of the TimePicker
     * ! Time can be in minute (default) or seconds units, look on 'useSeconds' option value
     */
    public override setValue(value: MINUTES_OR_SECONDS): void {
        if (this._isValidTime(value)) {
            this._value = value;
            this._setTimeDisplayedFromTime();

            this.refresh();
        }
    }

    /**
     * Init hoursList with list of values depending on hourAmPm option
     */
    private _initHourSelector(): void {
        let innerHTML;
        if (this.options.hourAmPm) {
            innerHTML = this._getListElementHTML(1, MAX_HOURS_DAY_LIMIT / 2, 1);// 1 => 12
        } else if (this.options.maxHoursLimit != null && this.options.maxHoursLimit !== TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT) {
            innerHTML = this._getListElementHTML(0, this.options.maxHoursLimit, 1);

            // Limit height to time-picker height
            this._components.hoursSelector.style.maxHeight = toPx(this.clientHeight);
        } else {
            innerHTML = this._getListElementHTML(0, MAX_HOURS_DAY_LIMIT - 1, 1);// 0 => 23
        }

        this._components.hoursSelector.innerHTML = innerHTML;
        this._components.hoursSelector.style.gridTemplateColumns = this.options.hourAmPm ? "repeat(3, 1fr)" : "repeat(6, 1fr)";
    }

    /**
     * Init minutesSelector with list of values (0 - 59)
     */
    private _initMinutesSelector(): void {
        this._components.minutesSelector.innerHTML = this._getListElementHTML(0, 55, 5);
        this._components.minutesSelector.style.gridTemplateColumns = "repeat(3, 1fr)";
    }

    /**
     * Return the list of span from start to end with value equal to span index
     * @param start begin on the list
     * @param end end on the list
     * @param step the step of the list
     */
    private _getListElementHTML(start: number, end: number, step: number): string {
        let html = "";
        let i = start;
        while (i <= end) {
            html += "<div class='selector-element' value='" + i + "'> " + NumberTools.twoDigits(i) + " </div>";
            i += step;
        }
        return html;
    }

    /** Refresh display based on given ranges */
    private _refreshDisplayFromRanges(): void {

        const isValueInTimeRangeHrUp = this._isTimeInMinutesInRanges(this._getValueInMinutes() + MINUTES_IN_ONE_HOUR);
        toggleClass(this._components.hrUpBtn, !isValueInTimeRangeHrUp, ARROW_DISABLED);

        const isValueInTimeRangeHrDown = this._isTimeInMinutesInRanges(this._getValueInMinutes() - MINUTES_IN_ONE_HOUR);
        toggleClass(this._components.hrDownBtn, !isValueInTimeRangeHrDown, ARROW_DISABLED);

        // Check minute after current time is still in range
        const isValueInTimeRangeMinUp = this._isTimeInMinutesInRanges(this._getValueInMinutes() + 1);
        toggleClass(this._components.minUpBtn, !isValueInTimeRangeMinUp, ARROW_DISABLED);

        // Check minute before current time is still in range
        const isValueInTimeRangeMinDown = this._isTimeInMinutesInRanges(this._getValueInMinutes() - 1);
        toggleClass(this._components.minDownBtn, !isValueInTimeRangeMinDown, ARROW_DISABLED);

        // If value of picker is 0 and not allowed in range, set to minimum range possible
        if (this._value === 0 && !this._isTimeInMinutesInRanges(this._getValueInMinutes()) && this.options.ranges) {

            const lowestRangeTime = this.options.ranges.reduce((prev, curr) => {
                return prev.startTime < curr.startTime ? prev : curr;
            });

            this.setValue(lowestRangeTime.startTime);
        }
    }

    /**
     * Update the displayed time of the TimePicker depending to mode 
     * If hourAmPm mode is enable, we change the hour displayed
     */
    public updateDisplayedTime(): void {

        if (this.options.hourAmPm) {
            const halfDayHours = HOURS_IN_ONE_DAY / 2;
            this._timeDisplayed.hours = this._timeDisplayed.hours % halfDayHours || halfDayHours;
        }

        if (this.options.minNegativeHoursLimit && this._timeDisplayed.hours < 0) {
            const numberDayBeforeJDay = Math.ceil(this._timeDisplayed.hours / -HOURS_IN_ONE_DAY);
            this._components.timeDisplayHoursLabel.innerText = `${this.i18n.labels.hours} (${this.i18n.labels.days}-${numberDayBeforeJDay})`;

            const hoursModulo = this._timeDisplayed.hours % -HOURS_IN_ONE_DAY;
            this._components.hoursValue.innerText = hoursModulo === 0 ? NumberTools.twoDigits(0) : NumberTools.twoDigits(HOURS_IN_ONE_DAY + hoursModulo);
        }

        else if (this._timeDisplayed.hours >= HOURS_IN_ONE_DAY) {
            const numberDayAfterJDay = Math.floor(this._timeDisplayed.hours / HOURS_IN_ONE_DAY);
            this._components.timeDisplayHoursLabel.innerText = `${this.i18n.labels.hours} (${this.i18n.labels.days}+${numberDayAfterJDay})`;
            this._components.hoursValue.innerText = NumberTools.twoDigits(this._timeDisplayed.hours % HOURS_IN_ONE_DAY);
        }

        else {
            this._components.timeDisplayHoursLabel.innerText = this.i18n.labels.hours;
            this._components.hoursValue.innerText = NumberTools.twoDigits(this._timeDisplayed.hours);
        }
        this._components.minutesValue.innerText = NumberTools.twoDigits(this._timeDisplayed.minutes);
    }

    /**
     * Increment timeInMinutes by 1 hour % 24h
     */
    private _incHr(): void {
        if (this.disabled) { return; }// Don't allow callback if component is disabled

        if (!this._isTimeInMinutesInRanges(this._getValueInMinutes() + MINUTES_IN_ONE_HOUR)) {
            return;
        }

        this._timeDisplayed.hours++;

        // Reduce hours to 24h
        if (this.options.minNegativeHoursLimit) {
            if ((this.options.maxHoursLimit != null && this._timeDisplayed.hours > this.options.maxHoursLimit) || (this.options.maxHoursLimit === null && this._timeDisplayed.hours > TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT)) {// If hours > maxHoursLimit => go to 0
                this._timeDisplayed.hours = this.options.minNegativeHoursLimit;
            }
        } else {
            if (this.options.maxHoursLimit === TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT) {// Default "one day"
                this._timeDisplayed.hours %= MAX_HOURS_DAY_LIMIT;
            } else if (this.options.maxHoursLimit != null && this._timeDisplayed.hours > this.options.maxHoursLimit) {// If hours > maxHoursLimit => go to 0
                this._timeDisplayed.hours = 0;
            }
        }


        this._setTimeInMinutesFromTimeDisplayed();
        this.updateDisplayedTime();
    }

    /**
     * Decrement timeInMinutes by 1 hour, go 23 hours if negative
     */
    private _downHr(): void {
        if (this.disabled) { return; }// Don't allow callback if component is disabled

        if (!this._isTimeInMinutesInRanges(this._getValueInMinutes() - MINUTES_IN_ONE_HOUR)) {
            return;
        }

        this._timeDisplayed.hours--;

        if ((this.options.minNegativeHoursLimit && this._timeDisplayed.hours < this.options.minNegativeHoursLimit) ||
            (!this.options.minNegativeHoursLimit && this._timeDisplayed.hours < 0)) {
            this._timeDisplayed.hours = this.options.maxHoursLimit !== TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT && this.options.maxHoursLimit != null ? this.options.maxHoursLimit : MAX_HOURS_DAY_LIMIT - 1;
        }

        this._setTimeInMinutesFromTimeDisplayed();
        this.updateDisplayedTime();
    }

    /**
     * Wheel Handler for hours
     */
    private _wheelHr(event: WheelEvent): void {
        if (this.disabled) { return; }// Don't allow callback if component is disabled

        event.preventDefault();
        if (event.deltaY > 0) {
            // Wheel Down
            this._downHr();
        } else {
            // Wheel up
            this._incHr();
        }
    }

    /**
     * Wheel Handler for minutes
     */
    private _wheelMinute(event: WheelEvent): void {
        if (this.disabled) { return; }// Don't allow callback if component is disabled

        event.preventDefault();
        if (event.deltaY > 0) {
            // Wheel Down
            this._downMin();
        } else {
            // Wheel up
            this._incMin();
        }
    }

    /**
     * Increment timeInMinutes by 1 minute % 60 minutes
     */
    private _incMin(): void {
        if (this.disabled) { return; }// Don't allow callback if component is disabled

        if (!this._isTimeInMinutesInRanges(this._getValueInMinutes() + 1)) {
            return;
        }

        this._timeDisplayed.minutes += 1;
        this._timeDisplayed.minutes %= MINUTES_IN_ONE_HOUR;
        this._setTimeInMinutesFromTimeDisplayed();
        this.updateDisplayedTime();
    }

    /**
     * Increment timeInMinutes by 1 minute, go 59 minutes if negative
     */
    private _downMin(): void {
        if (this.disabled) { return; }// Don't allow callback if component is disabled

        if (!this._isTimeInMinutesInRanges(this._getValueInMinutes() - 1)) {
            return;
        }

        this._timeDisplayed.minutes = this._timeDisplayed.minutes - 1 < 0 ? MINUTES_IN_ONE_HOUR - 1 : this._timeDisplayed.minutes -= 1;
        this._setTimeInMinutesFromTimeDisplayed();
        this.updateDisplayedTime();
    }

    /**
     * Add a "HrOrMin Button" click behavior for an element
     * @param element Element to add click listener
     */
    private _addHrOrMinButtonClickListener(element: HTMLElement): void {
        // Init mousedown behavior values
        let mouseDownInterval: number | null = null;

        const stopIntervalCallback = (evt: Event): void => {
            evt.stopPropagation();
            if (mouseDownInterval) {
                // Remove interval
                window.clearInterval(mouseDownInterval);
            }
            // Reset mousedown behavior values
            mouseDownInterval = null;
        };

        const onMouseDown = (evt: Event): void => {
            evt.stopPropagation();
            evt.preventDefault();
            // Set up an interval
            mouseDownInterval = window.setInterval(() => {
                // Call callback each {{MOUSE_CLICK_DEBOUNCE}} ms
                switch (element) {
                    case this._components.hrUpBtn:
                        this._incHr();
                        break;
                    case this._components.hrDownBtn:
                        this._downHr();
                        break;
                    case this._components.minUpBtn:
                        this._incMin();
                        break;
                    case this._components.minDownBtn:
                        this._downMin();
                        break;
                    default:
                        break;
                }
            }, this.MOUSE_CLICK_DEBOUNCE);
        };
        // Add click listener mouse down / touch start
        element.addEventListener("touchstart", onMouseDown);
        element.addEventListener("mousedown", onMouseDown);
        // Add clear interval listener when the press is finished
        element.addEventListener("mouseleave", stopIntervalCallback);
        element.addEventListener("mouseup", stopIntervalCallback);
        element.addEventListener("touchend", stopIntervalCallback);
        element.addEventListener("touchcancel", stopIntervalCallback);
    }

    /**
     * Display the hours selector or minutes selector
     * Hide the timeDisplay element
     * @param selector the selector we want to display
     */
    private _timeSelectorHandler(selector: TimePickerSelectorMode, event: MouseEvent): void {
        const targetElement = event.target as Element;
        const valueSelected = Number(targetElement.innerHTML);
        let selectedElement: HTMLElement | null = null;

        /** Toggle disable state of selectorContainers if needed */
        this._applyRangesToSelector(selector);

        if (selector === "hr") {
            setDisplay(this._components.hoursSelectorContainer, Display.FLEX);
            selectedElement = this._components.hoursSelectorContainer.querySelector("[value='" + valueSelected + "']");
        } else {
            setDisplay(this._components.minutesSelectorContainer, Display.FLEX);
            selectedElement = this._components.minutesSelectorContainer.querySelector("[value='" + valueSelected + "']");
        }

        if (selectedElement) {
            selectedElement.classList.add("selected");
        }

        // If selector header hide if required in options
        const selectorHeaders = this._components.timePickerContainer.querySelectorAll(".selector-header");
        if (selectorHeaders) {
            selectorHeaders.forEach((selector) => {
                toggleVisible(selector as HTMLElement, this.options.displaySelectorHeaderUnits, Display.FLEX);
            });
        }

        setDisplay(this._components.timeDisplay, Display.NONE);
    }

    /** Apply ranges  */
    private _applyRangesToSelector(valueSelector: TimePickerSelectorMode): void {

        const selectorContainer = valueSelector === "hr" ? this._components.hoursSelector : this._components.minutesSelector;
        const selectors = Array.from(selectorContainer.querySelectorAll(".selector-element")) as HTMLDivElement[];
        for (const selector of selectors) {
            const value = Number(selector.getAttribute("value"));
            toggleClass(selector, !this._isTimeInMinutesInRanges(this._getMinutesForRangeWithSelector(valueSelector, value)), SELECTOR_DISABLED);
        }
    }

    /**
     * Get time in minutes (or seconds, based on "useSeconds" option), for current selector
     * ex: if selector = "hour", value = 5, currentValue = 14h25 => return 5h25 
     * ex: if selector = "min", value = 36, currentValue = 8h51 => return 8h36 
     */
    private _getMinutesForRangeWithSelector(valueSelector: TimePickerSelectorMode, value: MINUTES | HOURS): MINUTES_OR_SECONDS {
        let minutesOfRange = 0;

        const currentValueParsed = convertTimeInMinutesToDaysHoursMinutes(this._getValueInMinutes() ?? 0);
        if (valueSelector === "hr") {
            minutesOfRange = hoursToMinutes(value) + currentValueParsed.minutes;
        } else if (valueSelector === "min") {
            minutesOfRange = hoursToMinutes(currentValueParsed.hours) + value;
        }
        return minutesOfRange;
    }

    /**
     * Hide all selectors and display the timeDisplay element
     */
    public resetTimePickerView(): void {
        if (this.disabled) { return; }// Don't allow callback if component is disabled

        this._components.timePickerContainer.querySelector(".selected")?.classList.remove("selected");
        this._components.hoursSelectorContainer.style.display = Display.NONE;
        this._components.minutesSelectorContainer.style.display = Display.NONE;
        this._components.timeDisplay.style.display = Display.FLEX;
    }

    /**
     * Depending to the mode (hr or minute), we get the value of the selectors element clicked and set timeInMinutes with the new displayed value
     * @param event 
     * @param mode the mode
     */
    private _onTimeSelectorClick(event: MouseEvent, mode: TimePickerSelectorMode): void {
        if (this.disabled) { return; }// Don't allow callback if component is disabled

        const targetElement = event.target as Element;
        const valueSelected = Number(targetElement.getAttribute("value"));

        // Check if the selected time is valid for ranges
        if (!this._isTimeInMinutesInRanges(this._getMinutesForRangeWithSelector(mode, valueSelected))) {
            return;
        }

        if (mode === "hr") {
            this._timeDisplayed.hours = valueSelected;
        } else {
            this._timeDisplayed.minutes = valueSelected;
        }
        this.resetTimePickerView();
        this._setTimeInMinutesFromTimeDisplayed();
        this.updateDisplayedTime();
    }

    /**
     * Return hour value depending to hourAmPm and amORpm options
     * if hourAmPm is enable, we have to check amORpm to know the real hour value
     * @param hour 
     */
    private _getHourValueFromHourMode(hour: HOURS): HOURS {
        let hourValue: HOURS;
        if (this.options.hourAmPm) {
            const halfDayHours = HOURS_IN_ONE_DAY / 2;
            if (this._amORpm === "am") {
                hourValue = hour % halfDayHours;
            } else {
                hourValue = hour % halfDayHours + halfDayHours;
            }
        } else {
            hourValue = hour;
        }
        return hourValue;

    }

    /**
     * If value of the radio change is different from current timeInMinutes option of the TimePicker (to prevent click on same radio)
     * We change the current timeInMinutes of the TimePicker from displayed value
     * @param event the event that trigger this handler
     */
    private _onClickAmPmBtn(event: Event): void {

        // Toggle _amORpm value
        this._amORpm = this._amORpm === "am" ? "pm" : "am";

        // Toggle display
        this._components.amPmValue.innerText = this._amORpm.toUpperCase();

        // Update time
        this._setTimeInMinutesFromTimeDisplayed();
    }

    /**
     * Return timeInMinutes passed is am or pm
     * @param timeInMinutes 
     */
    public isAmOrPm(timeInMinutes: number): AmOrPm {
        return Math.floor(timeInMinutes / MINUTES_IN_ONE_HOUR) < HOURS_IN_ONE_DAY / 2 ? "am" : "pm";
    }

    /**
     * Set timeInMinutes attributs from timeDisplayed and hourAmPm mode
     */
    private _setTimeInMinutesFromTimeDisplayed(): void {
        const valueInMinutes = this._getHourValueFromHourMode(this._timeDisplayed.hours) * MINUTES_IN_ONE_HOUR + this._timeDisplayed.minutes;
        this._value = this.useMinUnit() ? valueInMinutes : minutesToSeconds(valueInMinutes);// Convert minutes to seconds if needed

        // Refresh listeners and display disabled
        this._refreshDisplayFromRanges();
        this._dispatchOnPickerValueChange();

        this.setTimeInMinuteTimePickerEvent.emit({ ampm: this._amORpm });
    }

    /**
     * Set timeDisplayed From timeInMinutes
     */
    private _setTimeDisplayedFromTime(): void {
        const valueInMinutes = this._getValueInMinutes();
        const restOfMinutesInOneHour = valueInMinutes % MINUTES_IN_ONE_HOUR;

        this._timeDisplayed.hours = Math.floor(valueInMinutes / MINUTES_IN_ONE_HOUR);
        if (valueInMinutes >= 0) { // Normal behavior
            this._timeDisplayed.minutes = restOfMinutesInOneHour;
        } else if (restOfMinutesInOneHour === 0) {  // To get 00:00 instead of 00:60
            this._timeDisplayed.minutes = 0;
        } else {
            this._timeDisplayed.minutes = MINUTES_IN_ONE_HOUR + restOfMinutesInOneHour;
        }
    }

    /**
     * Checked if the time passed is valid (0 <= T < MINUTES_IN_ONE_DAY) for min and (0 <= T < SECONDS_IN_ONE_DAY) for seconds
     * Log error if failed
     * @param time to check
     */
    private _isValidTime(time: number | undefined): boolean {
        if (time == null || this.options.maxHoursLimit == null) { return false; }

        const isDayHoursLimitDifferentThanOneDay = this.options.maxHoursLimit !== TIME_PICKER_DEFAULT_MAX_HOURS_LIMIT;

        const isValidMins = time <= (isDayHoursLimitDifferentThanOneDay ? MINUTES_IN_ONE_HOUR * this.options.maxHoursLimit : MINUTES_IN_ONE_DAY);
        const isValidSeconds = time <= (isDayHoursLimitDifferentThanOneDay ? SECONDS_IN_ONE_HOUR * this.options.maxHoursLimit : SECONDS_IN_ONE_DAY);
        const isValidUnit = this.useMinUnit() ? isValidMins : isValidSeconds;

        if (!isValidUnit || (time < 0 && !this.options.minNegativeHoursLimit)) {
            this._log?.error(this.i18n.errorLog.timeInvalid.incorrectValue + " -->  0 <= " + time + " < " + (isDayHoursLimitDifferentThanOneDay ? this.options.maxHoursLimit * MINUTES_IN_ONE_HOUR : MINUTES_IN_ONE_DAY) + " " + this.i18n.errorLog.timeInvalid.notCorrect);
            return false;
        }

        return true;
    }

    /** @returns true if given time in minutes is in one range for picker */
    private _isTimeInMinutesInRanges(timeInMinutes: number | undefined): boolean {

        if (timeInMinutes == null) { return false; }

        // If no range given or empty, all range is enable
        if (!this.options.ranges || this.options.ranges.length === 0) { return true; }

        for (const range of this.options.ranges) {
            // Check valid ranges given
            if (!this._isValidTime(range.startTime) || !this._isValidTime(range.finishTime) || range.startTime > range.finishTime) {
                this._log?.error(this.i18n.errorLog.rangeInvalid.incorrectValue + ": " + range.startTime + "=>" + range.finishTime);
                return false;
            }

            const startRange = this.useMinUnit() ? range.startTime : secondsToMinutes(range.startTime);
            const finishRange = this.useMinUnit() ? range.finishTime : secondsToMinutes(range.finishTime);


            if (isBetween(timeInMinutes, startRange, finishRange, true)) {
                return true;
            }
        }

        return false;
    }

    /**
     * @returns true if picker is currently using time in minutes unit
     */
    public useMinUnit(): boolean {
        return !Boolean(this.options.useSeconds);
    }

    /**
     * Helps to return value in seconds unit to minutes
     * @returns value converted in min unit
     */
    private _getValueInMinutes(): MINUTES {
        return this.useMinUnit() ? this._value : Math.floor(secondsToMinutes(this._value));
    }

    /** Refresh label of timePicker */
    private _refreshI18nLabels(): void {
        this._components.hoursSelectorHoursLabel.innerText = this.i18n.labels.hours;
        this._components.hoursSelectorMinutesLabel.innerText = this.i18n.labels.minutes;
        this._components.timeDisplayHoursLabel.innerText = this.i18n.labels.hours;
        this._components.timeDisplayMinutesLabel.innerText = this.i18n.labels.minutes;
    }
}